import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CATS = [
  { key: "special", label: "Special Event" },
  { key: "community", label: "Community Event" },
  { key: "church", label: "Church Event" },
];

function toIso(local) {
  if (!local) return "";
  const d = new Date(local);
  if (isNaN(d)) return "";
  return d.toISOString();
}

function toLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * EventForm — shared form for creating or editing an event.
 * Used inline on the Events page AND on the standalone /post/event route.
 *
 * Props:
 *   - editingId? string — when present, PATCHes /events/{id} and loads initial values from API
 *   - onSaved(event) — called with the saved event doc after a successful POST/PATCH
 *   - onCancel() — user tapped cancel; parent decides what to do
 *   - submitLabel — override the primary button's label
 */
export default function EventForm({ editingId, onSaved, onCancel, submitLabel }) {
  const editing = Boolean(editingId);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("community");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [location, setLocation] = useState("");
  const [address, setAddress] = useState("");
  const [organizer, setOrganizer] = useState("");
  const [contact, setContact] = useState("");
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    api.get(`/events/${editingId}`).then(({ data }) => {
      setTitle(data.title || "");
      setDescription(data.description || "");
      setCategory(data.category || "community");
      setStart(toLocal(data.start_date));
      setEnd(toLocal(data.end_date));
      setLocation(data.location || "");
      setAddress(data.address || "");
      setOrganizer(data.organizer || "");
      setContact(data.contact || "");
      setImages(data.images || []);
    });
  }, [editing, editingId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim() || !start) {
      toast.error("Title, description and start date are required");
      return;
    }
    setSaving(true);
    const payload = {
      title,
      description,
      category,
      start_date: toIso(start),
      end_date: end ? toIso(end) : null,
      location,
      address,
      organizer,
      contact,
      images,
    };
    try {
      const { data } = editing
        ? await api.patch(`/events/${editingId}`, payload)
        : await api.post("/events", payload);
      toast.success(editing ? "Event updated" : "Event published");
      onSaved?.(data);
    } catch (err) {
      toast.error("Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6 bg-white border border-[#E2DED0] rounded-2xl p-6 sm:p-8" data-testid="event-form">
      <div>
        <Label>Cover photos</Label>
        <div className="mt-2"><ImageUploader value={images} onChange={setImages} max={4} /></div>
      </div>

      <div>
        <Label>Title</Label>
        <Input data-testid="event-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Eritrean Independence Day Celebration" />
      </div>

      <div>
        <Label>Category</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="event-category"><SelectValue /></SelectTrigger>
          <SelectContent>{CATS.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Starts</Label>
          <Input data-testid="event-start" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div>
          <Label>Ends (optional)</Label>
          <Input data-testid="event-end" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Location / Venue</Label>
          <Input data-testid="event-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Seattle Center Armory" />
        </div>
        <div>
          <Label>Address</Label>
          <Input data-testid="event-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="305 Harrison St, Seattle, WA" />
        </div>
      </div>

      <div>
        <Label>Description</Label>
        <Textarea data-testid="event-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={6} placeholder="What is this event about? Program, speakers, food, dress code…" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Organizer</Label>
          <Input data-testid="event-organizer" value={organizer} onChange={(e) => setOrganizer(e.target.value)} placeholder="ABM Community Team" />
        </div>
        <div>
          <Label>Contact</Label>
          <Input data-testid="event-contact" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Email or phone" />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" className="rounded-full" onClick={onCancel} data-testid="event-cancel">
            Cancel
          </Button>
        )}
        <Button data-testid="event-submit" type="submit" disabled={saving} className="abm-btn-pine rounded-full px-6">
          {saving ? "Saving…" : (submitLabel || (editing ? "Save changes" : "Publish event"))}
        </Button>
      </div>
    </form>
  );
}
